/**
 * ToastProvider — notificaciones efímeras, una sola implementación para toda la
 * app (web pública + portal). Reemplaza los `alert()` de feedback ("no se pudo
 * cancelar", "horario ocupado", etc.) por algo que no bloquea el hilo, no es
 * feo, y respeta el tema.
 *
 * Uso:
 *   const toast = useToast();
 *   toast.success('Consulta cancelada.');
 *   toast.error('No se pudo cancelar. Intentá de nuevo.');
 *
 * Se monta una vez en App.tsx envolviendo <Routes>. Renderiza el viewport vía
 * createPortal en <body> — nunca queda atrapado dentro de un modal scrolleado.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  leaving?: boolean;
}

interface ToastApi {
  success: (message: string, opts?: { duration?: number }) => void;
  error: (message: string, opts?: { duration?: number }) => void;
  info: (message: string, opts?: { duration?: number }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4500,
  info: 4500,
  error: 7000,
};

const ICON: Record<ToastVariant, string> = {
  success: 'fa-solid fa-circle-check',
  error: 'fa-solid fa-circle-exclamation',
  info: 'fa-solid fa-circle-info',
};

const ROLE: Record<ToastVariant, 'status' | 'alert'> = {
  success: 'status',
  info: 'status',
  error: 'alert',
};

let counter = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    // animación de salida antes del unmount
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, leaving: true } : t)));
    const t = setTimeout(() => {
      setToasts(prev => prev.filter(x => x.id !== id));
      timers.current.delete(id);
    }, 180);
    timers.current.set(id, t);
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, opts?: { duration?: number }) => {
      const id = ++counter;
      setToasts(prev => [...prev, { id, message, variant }]);
      const duration = opts?.duration ?? DEFAULT_DURATION[variant];
      const t = setTimeout(() => remove(id), duration);
      timers.current.set(id, t);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      info: (m, o) => push('info', m, o),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ccui-toast-viewport" aria-live="polite" aria-atomic="false">
          {toasts.map(t => (
            <div
              key={t.id}
              className={`ccui-toast ccui-toast--${t.variant}${t.leaving ? ' ccui-toast--leaving' : ''}`}
              role={ROLE[t.variant]}
            >
              <i className={`ccui-toast__icon ${ICON[t.variant]}`} aria-hidden="true" />
              <span className="ccui-toast__msg">{t.message}</span>
              <button
                type="button"
                className="ccui-toast__close"
                onClick={() => remove(t.id)}
                aria-label="Cerrar"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>');
  }
  return ctx;
}
